/*
 * File: CustomActivityTimeline.tsx
 * Project: a-a
 * File Created: Thursday, 19th December 2024 7:48:05 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Wednesday, 10th December 2025 7:00:15 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React from 'react';
import CustomTimeline from '../../CustomTimeline/CustomTimeline';
import { Box, Typography } from '@mui/material';
import zeplinColor from '../../../../theme/Color';

const CustomActivityTimeline = ({
  loading,
  val,
  newDataTotal,
  index,
}: {
  loading: boolean;
  val: any;
  newDataTotal: number;
  index: number;
}) => {
  const getDateDetails = (date: string, label: string) => (
    <Box sx={{ display: 'flex', gap: '4px', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography
        variant='responsiveBody2'
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {date
          ? new Date(date)?.toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : 'NA'}
      </Typography>
    </Box>
  );

  const getStageDetails = (stage: string, label: string) => (
    <Box sx={{ display: 'flex', gap: '4px', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography
        variant='responsiveBody2'
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {stage || 'NA'}
      </Typography>
    </Box>
  );
  const getStatusDetails = (status: string) => (
    <Box sx={{ display: 'flex', gap: '4px', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flexShrink: 0 }}>
        Status:
      </Typography>
      <Typography
        variant='responsiveBody2'
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {status || 'NA'}
      </Typography>
    </Box>
  );

  const getAmountDetails = (amount: string) => (
    <Box sx={{ display: 'flex', gap: '4px', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flexShrink: 0 }}>
        Amount:
      </Typography>
      <Typography
        variant='responsiveBody2'
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {amount || 'NA'}
      </Typography>
    </Box>
  );

  const getCreatedBy = (createdBy: string, label: string) => (
    <Box sx={{ display: 'flex', gap: '4px', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography
        variant='responsiveBody2'
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {createdBy || 'NA'}
      </Typography>
    </Box>
  );
  const getLayout = (value: string, label: string) => (
    <Box sx={{ display: 'flex', gap: '4px', maxWidth: '100%', overflow: 'hidden' }}>
      <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography
        variant='responsiveBody2'
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value || 'NA'}
      </Typography>
    </Box>
  );
  const getSubject = (type: string, val: any) => {
    const SubjectTypography = (content: React.ReactNode) => (
      <Typography
        variant='responsiveBody2'
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
          display: 'block',
        }}
      >
        {content}
      </Typography>
    );

    switch (type) {
      case 'new account':
        return SubjectTypography(
          <>
            New account <span style={{ fontWeight: 'bold' }}>{val?.account}</span> has been created.
          </>,
        );
      case 'new lead':
        return SubjectTypography(
          <>
            New Lead <span style={{ fontWeight: 'bold' }}>{val?.lead}</span> has been created.
          </>,
        );
      case 'lead stage changed':
        return SubjectTypography(
          <>
            Lead <span style={{ fontWeight: 'bold' }}>{val?.lead}</span> has been moved from{' '}
            <span style={{ fontWeight: 'bold' }}>{val?.oldStage}</span> to{' '}
            <span style={{ fontWeight: 'bold' }}>{val?.newStage}</span>.
          </>,
        );
      case 'deal amount changed':
        return SubjectTypography(
          <>
            The amount for deal <span style={{ fontWeight: 'bold' }}>{val?.deal}</span> has been
            updated
            <span style={{ fontWeight: 'bold' }}> ${val?.oldAmount || 0}</span> to{' '}
            <span style={{ fontWeight: 'bold' }}> ${val?.newAmount || 0}</span>.
          </>,
        );
      case 'deal stage changed':
        return SubjectTypography(
          <>
            Deal <span style={{ fontWeight: 'bold' }}>{val?.deal}</span> has been moved from{' '}
            <span style={{ fontWeight: 'bold' }}>{val?.oldStage}</span> to{' '}
            <span style={{ fontWeight: 'bold' }}>{val?.newStage}</span>.
          </>,
        );
      case 'new deal':
        return SubjectTypography(
          <>
            New Deal <span style={{ fontWeight: 'bold' }}>{val?.deal}</span> has been created.
          </>,
        );
      case 'lead activity':
        return SubjectTypography(
          <>
            New activity <span style={{ fontWeight: 'bold' }}>"{val?.activitySubject}"</span> has
            been added to the lead <span style={{ fontWeight: 'bold' }}>{val?.lead}</span>.
          </>,
        );
      default:
        return null;
    }
  };
  const type = val?.type?.toLocaleLowerCase();
  const dateField =
    type === 'lead stage changed' ||
    type === 'new lead' ||
    type === 'lead activity' ||
    type === 'new account'
      ? val?.date // For new account, new lead, and lead activity, use the "created date"
      : val?.dealCloseDate; // For deal-related events, use the "close date"

  const createdBy =
    type === 'new lead' ||
    type === 'lead activity' ||
    type === 'lead stage changed' ||
    type === 'deal amount changed' ||
    type === 'deal stage changed' ||
    type === 'new deal'
      ? getCreatedBy(
          val?.createdByLead || val?.dealCreatedBy || val?.createdByActivity || val?.createdByLead,
          'Created by',
        )
      : getCreatedBy(val?.createdBy, 'Created by');

  const dateDetails = getDateDetails(
    dateField,
    type === 'new lead' || type === 'lead activity' || type === 'new account'
      ? 'Created Date'
      : 'Close Date',
  );

  const stageDetails =
    type === 'lead stage changed' ||
    type === 'deal amount changed' ||
    type === 'deal stage changed' ||
    type === 'new deal'
      ? getStageDetails(val?.newStage || val?.dealStage, 'Stage')
      : null;

  const statusDetails = type === 'new lead' ? getStatusDetails(val?.leadStatus) : null;
  const subject = getSubject(type, val);
  const amountDetails =
    type === 'deal amount changed' || type === 'deal stage changed' || type === 'new deal'
      ? getAmountDetails(val?.dealAmount ? `$${val?.dealAmount}` : 'NA')
      : null;
  const modifiedBy =
    type === 'deal amount changed' || type === 'deal stage changed'
      ? getLayout(val?.dealModifiedBy, 'Modified by')
      : null;
  const oldStage = type === 'deal stage changed' ? getLayout(val?.oldStage, 'Old stage') : null;
  const oldAmount =
    type === 'deal amount changed' && val?.oldAmount
      ? getLayout(`$${val?.oldAmount}`, 'Old amount')
      : null;
  return (
    <CustomTimeline
      loading={loading}
      subject={subject}
      addedBy={createdBy}
      closeDate={dateDetails}
      stageDetails={stageDetails}
      statusDetails={statusDetails}
      link={val?.link}
      amountDetails={amountDetails}
      modifiedBy={modifiedBy}
      oldStage={oldStage}
      oldAmount={oldAmount}
      date={val?.date}
      newDataTotal={newDataTotal}
      index={index}
      source={val?.source}
    />
  );
};

export default CustomActivityTimeline;
